import {
    Alert,
    AppShell,
    Box,
    Container,
    Paper,
    Text,
    Typography,
    VisuallyHidden,
} from "@mantine/core";
import { useContext, useMemo, type FC, type PropsWithChildren } from "react";
import LayoutContext, { LayoutPermissionMode } from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import InlineLoading from "./loading/InlineLoading";
import Header from "./Header";
import NavMenu from "./NavMenu";
import SubMenus from "./SubMenus";
import hasAnyPermission from "src/utilities/hasAnyPermission";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppSettings from "src/AppSettings";
import Search from "./Search";

interface IDefaultLayoutProps extends PropsWithChildren {}

const DefaultLayoutInternal: FC<IDefaultLayoutProps> = ({ children }) => {
    const permissions = useCurrentUserPermissions();

    const layoutContext = useContext(LayoutContext);

    const permissionStatus = useMemo(() => {
        if (!layoutContext.permission) {
            return {
                noAccess: false,
                permissionDenied: false,
            };
        }

        if (!permissions) {
            return {
                noAccess: true,
                permissionDenied: false,
            };
        }

        let permissionDenied = false;

        if (permissions && permissions.length === 0 && layoutContext.permission) {
            permissionDenied = true;
        } else if (
            permissions.length > 0 &&
            layoutContext.permission &&
            layoutContext.permissionMode === LayoutPermissionMode.Any &&
            !hasAnyPermission(permissions, [layoutContext.permission], layoutContext.context)
        ) {
            permissionDenied = true;
        } else if (
            permissions.length > 0 &&
            layoutContext.permission &&
            (layoutContext.permissionMode === LayoutPermissionMode.Exact ||
                !layoutContext.permissionMode) &&
            !hasAnyPermission(permissions, [layoutContext.permission], layoutContext.context)
        ) {
            permissionDenied = true;
        }

        return {
            noAccess: false,
            permissionDenied: permissionDenied,
        };
    }, [
        permissions,
        layoutContext.permission,
        layoutContext.permissionMode,
        layoutContext.context,
    ]);

    if (permissionStatus.noAccess) {
        return (
            <>
                <Alert
                    title="No Access"
                    icon={<FontAwesomeIcon icon={AppSettings.icons.noAccess} />}
                    color="error"
                >
                    <Typography>
                        This user account has not been assigned any roles for this website.
                    </Typography>
                    <Typography>
                        If you believe that you should have access please contact TROG for help.
                    </Typography>
                </Alert>
                <VisuallyHidden>{children}</VisuallyHidden>
            </>
        );
    }

    if (permissionStatus.permissionDenied) {
        return (
            <>
                <Alert
                    color="warning"
                    title="Permission Denied"
                    icon={<FontAwesomeIcon icon={AppSettings.icons.error.warning} />}
                >
                    You do not have permission to view this page.
                </Alert>
                <VisuallyHidden>{children}</VisuallyHidden>
            </>
        );
    }

    return (
        <>
            {layoutContext.loading && <InlineLoading height={200} />}
            {children}
        </>
    );
};

const copyrightYear = new Date().getFullYear().toString();

const DefaultLayout: FC<IDefaultLayoutProps> = ({ children }) => {
    const layoutContext = useContext(LayoutContext);

    return (
        <AppShell mode="static" header={{ height: "auto" }} padding={0} withBorder={false}>
            <AppShell.Header>
                <Header />
                <NavMenu menuItems={layoutContext.menuItems} />
                <SubMenus subMenus={layoutContext.subMenus} />
            </AppShell.Header>
            <AppShell.Main>
                <Container size="xl">
                    <Paper withBorder radius={0} p="sm" mb="sm">
                        <DefaultLayoutInternal>{children}</DefaultLayoutInternal>
                        <Box
                            ta="center"
                            pt="sm"
                            mt="sm"
                            mx="-sm"
                            style={{ borderTop: "4px solid var(--mantine-color-primary-filled)" }}
                        >
                            <Text size="xs" color="dimmed">
                                Copyright&nbsp;&copy;&nbsp;{copyrightYear} Trans Tasman Radiation
                                Oncology Group (TROG).
                            </Text>
                        </Box>
                    </Paper>
                </Container>
                <Search />
            </AppShell.Main>
        </AppShell>
    );
};

export default DefaultLayout;
